import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, AlertTriangle, NotebookPen, Smile, FileBarChart, Flag, Pill, Pencil } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { CheckinPrompt } from "../components/CheckinPrompt";
import { SecureAccountCard } from "../components/SecureAccountCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from "recharts";

const SYMPTOMS = ["Anxiety", "Tremor", "Nausea", "Headache", "Insomnia", "Muscle tension", "Dizziness", "Low mood", "Irritability", "Fatigue"];
const SEDATIVES = ["Alcohol", "Opioids", "Other sedatives", "Sleeping tablets"];
const SUBSTANCE_INFO = {
  "Alcohol": { severity: "high", note: "Alcohol and benzodiazepines both depress the nervous system — together they sharply raise the risk of heavy sedation, blackouts and slowed breathing." },
  "Opioids": { severity: "critical", note: "Opioids with benzodiazepines is one of the most dangerous combinations and can stop your breathing. If opioids may be involved, carry naloxone and never use alone." },
  "Other sedatives": { severity: "high", note: "Other sedatives (e.g. pregabalin, gabapentin, sedating antihistamines) stack with benzos and increase drowsiness and overdose risk." },
  "Sleeping tablets": { severity: "high", note: "Z-drugs and sleeping tablets act much like benzodiazepines — combining them intensifies sedation and next-day grogginess." },
};
const MOODS = ["😔", "😟", "😐", "🙂", "😌"];

export default function Tracker() {
  const [logs, setLogs] = useState([]);
  const [milestones, setMilestones] = useState(null);
  const [benzos, setBenzos] = useState([]);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    dose_mg: "", mood: 3, sleep: 3, symptoms: [], other_substances: [], notes: "",
  });

  const load = async () => {
    try { const { data } = await api.get("/logs"); setLogs(data.logs); setMilestones(data.milestones); } catch {}
  };
  const loadProfile = async () => {
    try { const { data } = await api.get("/taper/profile"); setProfile(data || {}); } catch {}
  };
  useEffect(() => {
    load();
    loadProfile();
    api.get("/taper/benzos").then(({ data }) => setBenzos(data.benzos || data || [])).catch(() => {});
  }, []);

  const currentBenzo = profile?.current_benzo || profile?.starting_benzo || "";
  const hasProfile = profile && (profile.starting_benzo || profile.current_benzo);

  const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const save = async () => {
    try {
      await api.post("/logs", { ...form, dose_mg: form.dose_mg ? parseFloat(form.dose_mg) : null });
      toast.success("Daily log saved. Well done for checking in today.");
      window.dispatchEvent(new Event("taperpath:anon-data-saved"));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const showInteraction = form.other_substances.length > 0;
  const hasCritical = form.other_substances.includes("Opioids");
  const chart = logs.map((l) => ({ date: l.date.slice(5), dose: l.dose_mg, mood: l.mood, symptoms: (l.symptoms || []).length }));
  const flaggedDays = logs
    .filter((l) => (l.other_substances || []).length > 0)
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="space-y-8">
      <SecureAccountCard />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Progress Tracker</h1>
          <p className="mt-2 text-muted-foreground">Check in with yourself. There are no wrong entries here.</p>
        </div>
        <Link to="/app/tracker/summary" data-testid="weekly-summary-link" className="flex items-center gap-2 rounded-full border border-[#4A7c59]/30 bg-white px-5 py-3 text-sm font-semibold text-[#4A7c59] transition-transform duration-300 hover:-translate-y-0.5"><FileBarChart className="h-4 w-4" /> Progress summary</Link>
      </div>

      <CheckinPrompt />

      <TaperProfileCard profile={profile} benzos={benzos} onSaved={loadProfile} />

      {milestones && milestones.days_tracked > 0 && (
        <div className="rounded-2xl bg-gradient-to-r from-[#4A7c59]/10 to-[#8FB8DE]/10 p-6">
          <p className="font-head text-lg font-semibold text-[#2C363F]">You've checked in {milestones.days_tracked} {milestones.days_tracked === 1 ? "day" : "days"}. 🌱</p>
          <p className="text-sm text-muted-foreground">{milestones.total_reduction > 0 ? `That's ${milestones.total_reduction}mg lower ${currentBenzo ? `of ${currentBenzo} ` : ""}than where you began — you're still further along than day one.` : "Showing up is the hard part, and you're doing it."}</p>
        </div>
      )}

      <Tabs defaultValue="log">
        <TabsList className="rounded-full bg-muted p-1">
          <TabsTrigger value="log" data-testid="tab-log" className="rounded-full">Today's log</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends" className="rounded-full">Trends</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal" className="rounded-full">Journal</TabsTrigger>
        </TabsList>

        <TabsContent value="log" className="mt-4">
          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8 space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Date</Label><Input data-testid="input-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl py-6" /></div>
              <div><Label>{currentBenzo ? `${currentBenzo} dose taken today (mg)` : "Dose taken today (mg)"}</Label><Input data-testid="input-dose-log" type="number" value={form.dose_mg} onChange={(e) => setForm({ ...form, dose_mg: e.target.value })} placeholder="e.g. 8" className="mt-1 rounded-xl py-6" /></div>
            </div>

            <div>
              <Label className="flex items-center gap-2"><Smile className="h-4 w-4" /> Mood</Label>
              <div className="mt-2 flex gap-2" data-testid="mood-picker">
                {MOODS.map((m, i) => (
                  <button key={i} onClick={() => setForm({ ...form, mood: i + 1 })} className={`flex h-12 w-12 items-center justify-center rounded-full text-2xl transition-transform duration-200 ${form.mood === i + 1 ? "scale-110 bg-[#4A7c59]/15 ring-2 ring-[#4A7c59]" : "bg-muted hover:scale-105"}`}>{m}</button>
                ))}
              </div>
            </div>

            <div>
              <Label>Sleep quality (1 poor – 5 great)</Label>
              <div className="mt-2 flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setForm({ ...form, sleep: n })} className={`h-11 flex-1 rounded-xl text-sm font-semibold transition-colors duration-200 ${form.sleep === n ? "bg-[#8FB8DE] text-white" : "bg-muted text-muted-foreground"}`}>{n}</button>
                ))}
              </div>
            </div>

            <div>
              <Label>Physical & emotional symptoms</Label>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SYMPTOMS.map((s) => (
                  <label key={s} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-sm transition-colors duration-200 ${form.symptoms.includes(s) ? "border-[#4A7c59] bg-[#4A7c59]/5" : "border-border"}`}>
                    <Checkbox checked={form.symptoms.includes(s)} onCheckedChange={() => toggle("symptoms", s)} data-testid={`symptom-${s.toLowerCase().replace(/\s/g, "-")}`} />{s}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Did you use anything sedating alongside your benzo today?</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {SEDATIVES.map((s) => (
                  <label key={s} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-sm transition-colors duration-200 ${form.other_substances.includes(s) ? "border-[#E8907E] bg-[#E8907E]/5" : "border-border"}`}>
                    <Checkbox checked={form.other_substances.includes(s)} onCheckedChange={() => toggle("other_substances", s)} data-testid={`substance-${s.toLowerCase().replace(/\s/g, "-")}`} />{s}
                  </label>
                ))}
              </div>
              {showInteraction && (
                <div data-testid="interaction-warning" className={`mt-3 rounded-2xl border p-4 text-sm ${hasCritical ? "border-[#D96C5B] bg-[#D96C5B]/10 text-[#8a2f22]" : "border-[#E8907E]/50 bg-[#E8907E]/10 text-[#8a4436]"}`}>
                  <div className="flex items-center gap-2 font-head font-semibold">
                    <AlertTriangle className="h-5 w-5 shrink-0" />
                    Interaction flag{form.other_substances.length > 1 ? "s" : ""}{form.dose_mg ? " alongside today's benzo dose" : ""}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {form.other_substances.map((s) => (
                      <li key={s} className="flex gap-2" data-testid={`interaction-detail-${s.toLowerCase().replace(/\s/g, "-")}`}>
                        <span className="mt-0.5">{SUBSTANCE_INFO[s]?.severity === "critical" ? "🚨" : "•"}</span>
                        <span><strong>{s}:</strong> {SUBSTANCE_INFO[s]?.note}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2">This is information only, not a judgement. If you feel very drowsy, confused or struggle to breathe, seek urgent help — call 999 or use the Get Help Now button.</p>
                </div>
              )}
            </div>

            <div><Label>Notes</Label><Textarea data-testid="input-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="How was today? Anything you want to remember." className="mt-1 rounded-xl" /></div>

            <Button data-testid="save-log" onClick={save} className="rounded-full bg-[#4A7c59] py-6 hover:bg-[#3B6347]"><Save className="mr-2 h-4 w-4" /> Save today's log</Button>
          </div>
        </TabsContent>

        <TabsContent value="trends" className="mt-4 space-y-6">
          {flaggedDays.length > 0 && (
            <div data-testid="interaction-flags-card" className="rounded-2xl border border-[#E8907E]/40 bg-[#E8907E]/5 p-6">
              <div className="flex items-center gap-2">
                <Flag className="h-5 w-5 text-[#D96C5B]" />
                <h3 className="font-head font-semibold text-[#2C363F]">Interaction flags</h3>
                <span className="rounded-full bg-[#D96C5B]/10 px-2 py-0.5 text-xs font-medium text-[#8a2f22]">{flaggedDays.length} day{flaggedDays.length > 1 ? "s" : ""}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">Days you logged something sedating alongside your benzo. Shared for your awareness — never a judgement.</p>
              <div className="mt-4 space-y-2">
                {flaggedDays.slice(0, 8).map((l) => {
                  const critical = (l.other_substances || []).includes("Opioids");
                  return (
                    <div key={l.date} data-testid={`flag-day-${l.date}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-border bg-white px-4 py-2.5 text-sm">
                      <span className="font-medium text-[#2C363F]">{new Date(l.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
                      {l.dose_mg ? <span className="text-xs text-muted-foreground">benzo {l.dose_mg}mg</span> : null}
                      <span className="flex flex-wrap gap-1.5">
                        {(l.other_substances || []).map((s) => (
                          <span key={s} className={`rounded-full px-2 py-0.5 text-xs font-medium ${SUBSTANCE_INFO[s]?.severity === "critical" ? "bg-[#D96C5B]/15 text-[#8a2f22]" : "bg-[#E8907E]/15 text-[#8a4436]"}`}>{s}</span>
                        ))}
                      </span>
                      {critical && <span className="text-xs font-semibold text-[#8a2f22]">🚨 high-risk combo</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {logs.length === 0 ? <EmptyTrends /> : (
            <>
              <ChartCard title="Dose reduction curve"><ResponsiveContainer width="100%" height={240}><AreaChart data={chart} margin={{ left: -10 }}><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4A7c59" stopOpacity={0.3} /><stop offset="95%" stopColor="#4A7c59" stopOpacity={0} /></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" /><XAxis dataKey="date" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip /><Area type="monotone" dataKey="dose" stroke="#4A7c59" strokeWidth={3} fill="url(#g)" /></AreaChart></ResponsiveContainer></ChartCard>
              <div className="grid gap-6 lg:grid-cols-2">
                <ChartCard title="Mood trend"><ResponsiveContainer width="100%" height={200}><LineChart data={chart} margin={{ left: -20 }}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" /><XAxis dataKey="date" tick={{ fontSize: 12 }} /><YAxis domain={[1, 5]} tick={{ fontSize: 12 }} /><Tooltip /><Line type="monotone" dataKey="mood" stroke="#8FB8DE" strokeWidth={3} dot={{ r: 3 }} /></LineChart></ResponsiveContainer></ChartCard>
                <ChartCard title="Symptom count"><ResponsiveContainer width="100%" height={200}><LineChart data={chart} margin={{ left: -20 }}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" /><XAxis dataKey="date" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip /><Line type="monotone" dataKey="symptoms" stroke="#E8907E" strokeWidth={3} dot={{ r: 3 }} /></LineChart></ResponsiveContainer></ChartCard>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="journal" className="mt-4"><Journal /></TabsContent>
      </Tabs>
    </div>
  );
}

const ChartCard = ({ title, children }) => (
  <div className="rounded-2xl border border-border bg-white p-6 shadow-sm"><h3 className="mb-4 font-head font-semibold text-[#2C363F]">{title}</h3>{children}</div>
);

const OTHER = "__other__";
function BenzoField({ testid, names, sel, setSel, value, setValue }) {
  return (
    <div className="space-y-2">
      <Select value={sel} onValueChange={(v) => { setSel(v); setValue(v === OTHER ? "" : v); }}>
        <SelectTrigger data-testid={testid} className="rounded-xl py-6"><SelectValue placeholder="Choose your medication" /></SelectTrigger>
        <SelectContent>
          {names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          <SelectItem value={OTHER}>Something else…</SelectItem>
        </SelectContent>
      </Select>
      {sel === OTHER && (
        <Input data-testid={`${testid}-other`} value={value} onChange={(e) => setValue(e.target.value)} placeholder="Type your medication name" className="rounded-xl py-6" />
      )}
    </div>
  );
}

function TaperProfileCard({ profile, benzos, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ starting_benzo: "", starting_dose: "", current_benzo: "", start_date: "" });

  const names = (benzos || []).map((b) => b.name);
  const asSelect = (val) => (val && !names.includes(val) ? OTHER : (val || ""));

  useEffect(() => {
    if (profile) setForm({
      starting_benzo: profile.starting_benzo || "",
      starting_dose: profile.starting_dose != null ? String(profile.starting_dose) : "",
      current_benzo: profile.current_benzo || "",
      start_date: profile.start_date || "",
    });
  }, [profile]);

  const hasProfile = profile && (profile.starting_benzo || profile.current_benzo);

  const [startSel, setStartSel] = useState("");
  const [curSel, setCurSel] = useState("");
  const openEditor = () => {
    setStartSel(asSelect(form.starting_benzo));
    setCurSel(asSelect(form.current_benzo));
    setEditing(true);
  };

  const save = async () => {
    if (!form.starting_benzo.trim() && !form.current_benzo.trim()) {
      toast.error("Add at least the medication you're tapering.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/taper/profile", {
        starting_benzo: form.starting_benzo.trim(),
        starting_dose: form.starting_dose ? parseFloat(form.starting_dose) : null,
        current_benzo: (form.current_benzo || form.starting_benzo).trim(),
        start_date: form.start_date || "",
      });
      toast.success("Your taper details are saved.");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!editing) {
    if (!hasProfile) {
      return (
        <div data-testid="taper-profile-prompt" className="rounded-2xl border border-dashed border-[#4A7c59]/40 bg-[#4A7c59]/5 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#4A7c59] text-white"><Pill className="h-5 w-5" /></div>
            <div className="flex-1">
              <h3 className="font-head text-lg font-semibold text-[#2C363F]">Set up your taper</h3>
              <p className="mt-1 text-sm text-muted-foreground">Tell us which benzodiazepine you're tapering and your starting dose. We'll add this to your logs and summaries so your GP or support worker sees the full picture.</p>
              <Button data-testid="taper-profile-setup-btn" onClick={openEditor} className="mt-4 rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">Add your taper details</Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div data-testid="taper-profile-card" className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#4A7c59]/10 text-[#4A7c59]"><Pill className="h-5 w-5" /></div>
            <div>
              <h3 className="font-head text-lg font-semibold text-[#2C363F]">Your taper</h3>
              <p className="mt-1 text-sm text-[#2C363F]" data-testid="taper-profile-summary">
                {profile.starting_benzo ? <>Started on <span className="font-semibold">{profile.starting_benzo}{profile.starting_dose != null ? ` at ${profile.starting_dose} mg` : ""}</span>{profile.start_date ? ` (from ${new Date(profile.start_date).toLocaleDateString("en-GB")})` : ""}. </> : null}
                {profile.current_benzo ? <>Currently tapering with <span className="font-semibold">{profile.current_benzo}</span>.</> : null}
              </p>
            </div>
          </div>
          <Button data-testid="taper-profile-edit-btn" variant="ghost" size="sm" onClick={openEditor} className="shrink-0 text-[#4A7c59]"><Pencil className="mr-1 h-4 w-4" /> Edit</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="taper-profile-form" className="rounded-2xl border border-[#4A7c59]/30 bg-white p-6 shadow-sm sm:p-8 space-y-5">
      <div className="flex items-center gap-2">
        <Pill className="h-5 w-5 text-[#4A7c59]" />
        <h3 className="font-head text-lg font-semibold text-[#2C363F]">Your taper details</h3>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Medication you started on</Label>
          <div className="mt-1"><BenzoField testid="starting-benzo-select" names={names} sel={startSel} setSel={setStartSel} value={form.starting_benzo} setValue={(v) => setForm((f) => ({ ...f, starting_benzo: v }))} /></div>
        </div>
        <div>
          <Label>Starting dose (mg)</Label>
          <Input data-testid="starting-dose-input" type="number" value={form.starting_dose} onChange={(e) => setForm({ ...form, starting_dose: e.target.value })} placeholder="e.g. 20" className="mt-1 rounded-xl py-6" />
        </div>
        <div>
          <Label>Medication you're currently tapering with</Label>
          <div className="mt-1"><BenzoField testid="current-benzo-select" names={names} sel={curSel} setSel={setCurSel} value={form.current_benzo} setValue={(v) => setForm((f) => ({ ...f, current_benzo: v }))} /></div>
          <p className="mt-1 text-xs text-muted-foreground">Leave the same as your starting medication if you haven't switched.</p>
        </div>
        <div>
          <Label>Start date (optional)</Label>
          <Input data-testid="taper-start-date" type="date" value={form.start_date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-xl py-6" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="taper-profile-save-btn" onClick={save} disabled={saving} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save taper details"}</Button>
        {hasProfile && <Button data-testid="taper-profile-cancel-btn" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
      </div>
    </div>
  );
}

const EmptyTrends = () => (
  <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center">
    <img src="https://images.pexels.com/photos/35283674/pexels-photo-35283674.jpeg" alt="Sunrise path" className="mx-auto mb-4 h-40 w-full max-w-sm rounded-2xl object-cover" />
    <p className="font-head text-lg font-semibold text-[#2C363F]">Your charts will grow here</p>
    <p className="text-sm text-muted-foreground">Save a few daily logs and you'll start to see your journey take shape.</p>
  </div>
);

function Journal() {
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get("/journal").then(({ data }) => setEntries(data.entries)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!text.trim()) return;
    try { await api.post("/journal", { text }); setText(""); toast.success("Journal entry saved."); window.dispatchEvent(new Event("taperpath:anon-data-saved")); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <Label className="flex items-center gap-2"><NotebookPen className="h-4 w-4" /> A space for your thoughts</Label>
        <Textarea data-testid="journal-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write freely — this is just for you." className="mt-2 min-h-28 rounded-xl" />
        <Button data-testid="journal-save" onClick={add} className="mt-3 rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">Save entry</Button>
      </div>
      {entries.map((e) => (
        <div key={e.id} className="rounded-2xl border border-border bg-white p-5">
          <p className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("en-GB")}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[#2C363F]">{e.text}</p>
        </div>
      ))}
    </div>
  );
}
